import {
	createCsrfMiddleware,
	createMiddleware,
	createStart,
} from "@tanstack/react-start";
import {
	isPlatformHost,
	routeCustomHost,
} from "../modules/commerce/domains/host-routing";
import { DEFAULT_SECURITY_HEADERS } from "./server/csp";

/**
 * Exactly one CSP per response (ADR-5.5): routes that need their own policy set it (the
 * hosted chat page does, through its route `headers`), and everything else gets the default
 * here. Same for Referrer-Policy: /c/* sends no-referrer, so it is not a route rule (Nitro's
 * route rules override what a route sets).
 */
const securityHeaders = createMiddleware().server(async ({ next }) => {
	const result = await next();
	const missing = Object.entries(DEFAULT_SECURITY_HEADERS).filter(
		([name]) => !result.response.headers.has(name),
	);
	if (missing.length === 0) {
		return result;
	}
	try {
		for (const [name, value] of missing) {
			result.response.headers.set(name, value);
		}
		return result;
	} catch {
		// Immutable headers (Response.redirect, a proxied fetch): copy once.
		const response = new Response(result.response.body, result.response);
		for (const [name, value] of missing) {
			response.headers.set(name, value);
		}
		return { ...result, response };
	}
});

/**
 * A retailer's custom domain (ADR-11) serves that retailer's chat only: `/` goes to it and
 * everything else is a 404 (routeCustomHost). A hostname that has a row but is not ACTIVE yet
 * (or any more) answers "not live" on every path: Cloudflare may already send its traffic
 * before the next poll marks it live, and it must never reach the app. A host without a row,
 * or with a REMOVED one, is ours. One indexed lookup per request.
 */
const customHost = createMiddleware().server(async ({ request, next }) => {
	const url = new URL(request.url);
	const hostname = url.hostname.replace(/\.$/, "");
	const [{ getBaseUrl }, { db }] = await Promise.all([
		import("@repo/utils"),
		import("@repo/database"),
	]);
	if (isPlatformHost(hostname, new URL(getBaseUrl()).hostname)) {
		return next();
	}
	const domain = await db.customDomain.findUnique({
		where: { hostname },
		select: { status: true, organization: { select: { slug: true } } },
	});
	if (!domain || domain.status === "REMOVED") {
		return next();
	}
	const { slug } = domain.organization;
	const route = routeCustomHost(
		url.pathname,
		slug ?? "",
		domain.status === "ACTIVE" && slug !== null,
	);
	if (route.type === "redirect") {
		return new Response(null, {
			status: 307,
			headers: { location: `${route.location}${url.search}` },
		});
	}
	if (route.type !== "serve") {
		return new Response(
			route.type === "not-live"
				? "This chat is not live on this domain yet."
				: "Not found",
			{
				status: 404,
				headers: { "content-type": "text/plain; charset=utf-8" },
			},
		);
	}
	return next();
});

/**
 * Start's router answers a request whose Accept names no HTML with 500 "Only HTML requests are
 * supported here", even for a path nothing serves. Package managers and API clients probe
 * unknown URLs that way, so they get a 404. Same Accept test as Start's; HTML requests pass
 * through untouched, and the body is read only for that one 500.
 */
const ONLY_HTML = "Only HTML requests are supported here";
export const htmlOnlyIsNotFound = createMiddleware().server(
	async ({ request, next }) => {
		const accepts = (request.headers.get("accept") || "*/*").split(",");
		if (accepts.some((part) => /^\s*(\*\/\*|text\/html)/.test(part))) {
			return next();
		}
		const result = await next();
		if (result.response.status !== 500) {
			return result;
		}
		const body: unknown = await result.response
			.clone()
			.json()
			.catch(() => null);
		if ((body as { error?: unknown } | null)?.error !== ONLY_HTML) {
			return result;
		}
		return {
			...result,
			response: Response.json({ error: "Not found" }, { status: 404 }),
		};
	},
);

export const startInstance = createStart(() => ({
	requestMiddleware: [
		// What Start applies by default when no start instance exists; kept.
		createCsrfMiddleware({
			filter: (ctx) => ctx.handlerType === "serverFn",
		}),
		securityHeaders,
		customHost,
		htmlOnlyIsNotFound,
	],
}));
